import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAddresses, saveAddress, setDefaultAddress, deleteAddress } from '@/api';
import type { Address } from '@/api';

const emptyForm = {
  name: '',
  phone: '',
  province: '',
  city: '',
  district: '',
  detail: '',
};

export default function AddressPage() {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  //加载地址列表
  const loadAddresses = useCallback(async () => {
    try {
      const list = await getAddresses();
      setAddresses(list || []);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const openAdd = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm);
    setErrors({});
    setShowModal(true);
  }, []);

  const openEdit = useCallback((addr: Address) => {
    setEditingId(addr.id);
    setForm({
      name: addr.name,
      phone: addr.phone,
      province: addr.province,
      city: addr.city,
      district: addr.district,
      detail: addr.detail,
    });
    setErrors({});
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    setErrors({});
  }, []);

  const validate = useCallback(() => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = '请输入收货人姓名';
    if (!form.phone.trim()) newErrors.phone = '请输入联系手机号';
    if (!/^(1[3-9]\d{9})$/.test(form.phone.trim())) newErrors.phone = '手机号格式不正确';
    if (!form.province.trim()) newErrors.province = '请输入省份';
    if (!form.city.trim()) newErrors.city = '请输入城市';
    if (!form.district.trim()) newErrors.district = '请输入区县';
    if (!form.detail.trim()) newErrors.detail = '请输入详细地址';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form]);

  //新增/编辑保存
  const handleSave = useCallback(async () => {
    if (!validate() || saving) return;
    setSaving(true);
    try {
      await saveAddress({
        id: editingId ? Number(editingId) : undefined,
        name: form.name.trim(),
        phone: form.phone.trim(),
        province: form.province.trim(),
        city: form.city.trim(),
        district: form.district.trim(),
        detail: form.detail.trim(),
        isDefault: 0,
      });
      showToast(editingId ? '保存成功' : '添加成功');
      closeModal();
      loadAddresses();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }, [validate, saving, editingId, form, closeModal, loadAddresses]);

  const handleDelete = useCallback((id: string) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  }, []);

  //确认删除
  const confirmDelete = useCallback(async () => {
    if (!deleteId || deleting) return;
    setDeleting(true);
    try {
      await deleteAddress(Number(deleteId));
      showToast('删除成功');
      setShowDeleteConfirm(false);
      setDeleteId(null);
      loadAddresses();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '删除失败');
    } finally {
      setDeleting(false);
    }
  }, [deleteId, deleting, loadAddresses]);

  //设为默认
  const setDefault = useCallback(async (id: string) => {
    try {
      await setDefaultAddress(Number(id));
      showToast('已设为默认地址');
      loadAddresses();
    } catch (err) {
      showToast(err instanceof Error ? err.message : '操作失败');
    }
  }, [loadAddresses]);

  const handleInputChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const fields = [
    { key: 'name', label: '收货人', placeholder: '请输入收货人姓名', type: 'text' },
    { key: 'phone', label: '手机号', placeholder: '请输入联系手机号', type: 'tel' },
    { key: 'province', label: '省份', placeholder: '如：广东省', type: 'text' },
    { key: 'city', label: '城市', placeholder: '如：深圳市', type: 'text' },
    { key: 'district', label: '区县', placeholder: '如：南山区', type: 'text' },
    { key: 'detail', label: '详细地址', placeholder: '街道、楼栋、门牌号等', type: 'text' },
  ];

  return (
    <div className="mobile-container pb-10">
      {/* Toast */}
      {toast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 bg-foreground-800 text-white text-sm rounded-full shadow-lg">
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-green-400"></i>
            <span>{toast}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-sm border-b border-background-100">
        <div className="flex items-center justify-between px-4 py-3.5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl text-foreground-700"></i>
            </button>
            <h1 className="text-base font-semibold text-foreground-900">我的地址</h1>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 active:scale-[0.95] transition-all cursor-pointer"
          >
            <i className="ri-add-line text-sm"></i>
            <span>新增</span>
          </button>
        </div>
      </header>

      <div className="px-4 pt-4 space-y-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-loader-4-line text-primary-400 text-3xl animate-spin mb-3" />
            <p className="text-foreground-400 text-sm">加载中...</p>
          </div>
        ) : addresses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-map-pin-2-line text-foreground-300 text-3xl"></i>
            </div>
            <p className="text-foreground-700 text-sm font-semibold mb-1">暂无收货地址</p>
            <p className="text-foreground-400 text-xs text-center mb-5">
              添加收货地址后，购物更便捷
            </p>
            <button
              onClick={openAdd}
              className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              新增地址
            </button>
          </div>
        ) : (
          addresses.map((addr) => (
            <div
              key={addr.id}
              className="bg-white rounded-2xl border border-background-100 px-4 py-4 hover:border-background-200 transition-colors"
            >
              {/* Top row */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-foreground-900 text-sm font-semibold">{addr.name}</span>
                  <span className="text-foreground-500 text-sm">{addr.phone}</span>
                  {addr.isDefault && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-primary-50 text-primary-500 text-[10px] font-medium">
                      默认
                    </span>
                  )}
                </div>
              </div>

              {/* Address */}
              <p className="text-foreground-600 text-xs leading-relaxed mb-3">
                {addr.province} {addr.city} {addr.district} {addr.detail}
              </p>

              {/* Actions */}
              <div className="h-px bg-background-100 mb-3"></div>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => !addr.isDefault && setDefault(addr.id)}
                  className={`flex items-center gap-1 text-xs font-medium transition-colors cursor-pointer ${
                    addr.isDefault
                      ? 'text-primary-500'
                      : 'text-foreground-400 hover:text-primary-500'
                  }`}
                >
                  <i
                    className={`${
                      addr.isDefault ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'
                    } text-sm`}
                  ></i>
                  <span>{addr.isDefault ? '默认地址' : '设为默认'}</span>
                </button>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => openEdit(addr)}
                    className="flex items-center gap-1 text-xs text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-2-line text-sm"></i>
                    <span>编辑</span>
                  </button>
                  <button
                    onClick={() => handleDelete(addr.id)}
                    className="flex items-center gap-1 text-xs text-foreground-500 hover:text-red-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                    <span>删除</span>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeModal}></div>
          <div className="relative bg-white rounded-t-2xl w-full max-w-[430px] animate-slide-up">
            {/* Modal header */}
            <div className="flex items-center justify-center px-4 py-3 border-b border-background-100">
              <div className="w-10 h-1 rounded-full bg-background-300"></div>
            </div>
            <div className="px-5 py-3">
              <h2 className="text-base font-semibold text-foreground-900 text-center">
                {editingId ? '编辑地址' : '新增地址'}
              </h2>
            </div>

            {/* Form */}
            <div className="px-5 pb-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    {f.label}
                  </label>
                  <input
                    type={f.type}
                    value={form[f.key as keyof typeof form]}
                    onChange={(e) => handleInputChange(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className={`w-full px-4 py-3 rounded-xl border text-sm outline-none transition-colors placeholder:text-foreground-300 ${
                      errors[f.key]
                        ? 'border-red-300 bg-red-50/50 focus:border-red-400'
                        : 'border-background-200 bg-white focus:border-primary-300'
                    }`}
                  />
                  {errors[f.key] && (
                    <p className="text-red-500 text-xs mt-1 pl-1">{errors[f.key]}</p>
                  )}
                </div>
              ))}

              {/* Buttons */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={closeModal}
                  className="flex-1 py-3.5 rounded-xl border border-background-200 text-foreground-700 text-sm font-medium hover:bg-background-50 transition-colors cursor-pointer"
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-3.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 active:scale-[0.98] transition-all cursor-pointer shadow-sm shadow-primary-500/20 disabled:opacity-60 flex items-center justify-center gap-1.5"
                >
                  {saving && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                  {editingId ? '保存修改' : '保存地址'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowDeleteConfirm(false)}></div>
          <div className="relative bg-white rounded-2xl w-full max-w-[320px] p-5 text-center">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-3">
              <i className="ri-delete-bin-line text-red-400 text-xl"></i>
            </div>
            <h3 className="text-base font-semibold text-foreground-900 mb-1">确认删除？</h3>
            <p className="text-foreground-400 text-xs mb-5">删除后无法恢复，请确认是否删除</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 rounded-xl border border-background-200 text-foreground-600 text-sm font-medium hover:bg-background-50 transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-1.5"
              >
                {deleting && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                删除
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
